import { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { getResourceDependencies } from "api/ResourceDependenciesApi";

export const useResourceDependencies = ({
  sourceId,
  direction = "forward",
  targetResourceType,
  shouldFetch = true,
  returnRows = false
}) => {
  const { user } = useAuth0();
  const [dependencies, setDependencies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchedKey, setFetchedKey] = useState(null);
  const queryKey = `${user?.stripeId ?? ""}:${sourceId ?? ""}:${direction ?? ""}:${targetResourceType ?? ""}:${returnRows}`;

  const fetchDependencies = useCallback(async () => {
    if (!shouldFetch || !user?.stripeId || !sourceId || !direction) {
      setDependencies([]);
      setHasFetched(false);
      setFetchedKey(null);
      return [];
    }

    setIsLoading(true);
    setError(null);
    setHasFetched(false);

    try {
      const response = await getResourceDependencies({
        tenantId: user.stripeId,
        sourceId,
        direction,
        targetResourceType
      });

      const rows = Array.isArray(response?.resultObject)
        ? response.resultObject
        : [];
      const nextDependencies = returnRows
        ? rows
        : [...new Set(rows.map((dependency) => dependency.target_id))];

      setDependencies(nextDependencies);
      setHasFetched(true);
      setFetchedKey(queryKey);
      return nextDependencies;
    } catch (error) {
      setError(error);
      setDependencies([]);
      setHasFetched(true);
      setFetchedKey(queryKey);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [
    user?.stripeId,
    sourceId,
    direction,
    targetResourceType,
    shouldFetch,
    returnRows,
    queryKey
  ]);

  useEffect(() => {
    fetchDependencies();
  }, [fetchDependencies]);

  return {
    dependencies,
    setDependencies,
    fetchDependencies,
    isLoading,
    hasFetched: hasFetched && fetchedKey === queryKey,
    error
  };
};
